import { useEffect, useState } from 'react';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import axios from 'src/utils/axios';
import { LoadingScreen } from 'src/components/loading-screen';
import { enqueueSnackbar } from 'notistack';
import { ConfirmDialog } from 'src/components/custom-dialog';
import LinearProgress from '@mui/material/LinearProgress';

const Downloads = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true); // for general loading (e.g. fetching or deletion)
  const [downloadingId, setDownloadingId] = useState(null); // track which video is downloading
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    getAllVideos();
  }, []);

  const getAllVideos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/all-videos`, {
        headers: { "Cache-Control": 'no-cache' },
      });
      if (response.status === 200 && response.data.metadata.success) {
        setVideos(response.data.payload);
      } else {
        console.error("Error in response data:", response.data);
      }
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  function formatDateTime(dateString) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().replace('T', ' ').substring(0, 19);
  }

  const handleDeleteClick = (video) => {
    setSelectedVideo(video);
    setConfirmOpen(true);
  };

  const deleteVideo = async () => {
    if (!selectedVideo) return;
    try {
      setLoading(true);
      const response = await axios.delete(`/videos/${selectedVideo.id}`, {
        headers: { "Cache-Control": 'no-cache' },
      });
      if (response.status === 200 && response.data.metadata.success) {
        enqueueSnackbar("Deleted Successfully", { variant: "error" });
        setVideos((prevVideos) => prevVideos.filter((video) => video.id !== selectedVideo.id));
      } else {
        console.error("Error deleting video:", response.data);
      }
    } catch (error) {
      console.error("Error deleting video:", error);
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  const handleDownload = async (videoUrl, name, videoId) => {
    console.log("🚀 ~ handleDownload ~ videoUrl:", videoUrl)
    if (!videoUrl) {
      console.error("No video found to download.");
      return;
    }
    try {
      setDownloadingId(videoId);
      const backendUrl = `https://epic-image-download.vercel.app/download?imageUrl=${encodeURIComponent(videoUrl)}`;
      const response = await fetch(backendUrl, { method: "GET" });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${name}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the video:", error);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
        Your Downloads
      </Typography>

      { loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <LoadingScreen size={60} />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ marginTop: 2, borderRadius: 1, boxShadow: 3 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f4f4f4' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Progress</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Request Date</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#333' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              { videos?.map((item, index) => (
                <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                  <TableCell>{item?.project_name}-{item?.camera_name}</TableCell>
                  <TableCell>{item?.status}</TableCell>
                  <TableCell sx={{ typography: 'caption', color: 'text.secondary' }}>
                    <LinearProgress
                      variant="determinate"
                      value={item?.progress || 0}
                      color={
                        (item?.progress <= 10 && 'error') ||
                        (item?.progress <= 30 && 'warning') ||
                        (item?.progress <= 50 && 'secondary') ||
                        (item?.progress <= 70 && 'info') ||
                        (item?.progress <= 90 && 'primary') ||
                        'success'
                      }
                      sx={{ mb: 1, height: 6, maxWidth: 80 }}
                    />
                    {item?.progress}%
                  </TableCell>
                  <TableCell>{formatDateTime(item?.created_at)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {item?.status !== "failed to create video" && (
                        <LoadingButton
                          loading={downloadingId === item.id}
                          variant="contained"
                          sx={{ width: '100px', padding: '8px', borderRadius: 1 }}
                          onClick={() => handleDownload(item?.path, item?.project_name, item?.id)}
                        >
                          Download
                        </LoadingButton>
                      )}
                      { item?.path && !item?.path.includes("video_in_queue") && (
                        <LoadingButton
                          loading={loading}
                          color="error"
                          variant="contained"
                          sx={{ width: '100px', padding: '8px', borderRadius: 1 }}
                          onClick={() => handleDeleteClick(item)}
                        >
                          Delete
                        </LoadingButton>
                      ) }
                    </Box>
                  </TableCell>
                </TableRow>
              )) }
            </TableBody>
          </Table>
        </TableContainer>
      ) }

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Delete Video"
        content={<Typography>Are you sure you want to delete this video?</Typography>}
        action={
          <LoadingButton
            loading={loading}
            variant="contained"
            color="error"
            onClick={deleteVideo}
          >
            Delete
          </LoadingButton>
        }
      />
    </Box>
  );
};

export default Downloads;
